/**
 * Wizard Step 6 — Successor soft-ask + final submission.
 *
 * Collects the user's designated successor (the one person with full
 * access to their vault who executes their wishes). Only firstName +
 * email here — the full setup (last name, phone, secret passcode,
 * validation Q&A, access permissions) happens in AddSuccessorScreen,
 * which the Priorities Reveal screen routes into with these values
 * pre-filled.
 *
 * Skipping is equally weighted with continuing so users don't feel
 * cornered into naming their successor before they're ready. A skip
 * surfaces a priority task on the dashboard instead.
 *
 * This is the final wizard screen — "Finish setup" POSTs the full
 * WizardAnswersDto to /api/onboarding/answers and routes the user to
 * the Priorities Reveal screen with the server response.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TextInput, Alert } from 'react-native';
import { useNavigation, CommonActions } from '@react-navigation/native';
import { WizardShell } from '../components/WizardShell';
import { useWizard } from '../WizardContext';
import { wizardService } from '../../../services/wizard';
import { ms } from '../../../utils/responsive';
import { createLogger } from '../../../utils/logger';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';

const logger = createLogger('WizardSuccessorSoftAsk');
const WIZARD_STEP = 6;
const TOTAL_STEPS = 6;

const emailLooksValid = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const WizardSuccessorSoftAskScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { answers, setSuccessor, setSubmission } = useWizard();
  const [firstName, setFirstName] = useState(answers.successor?.firstName ?? '');
  const [email, setEmail] = useState(answers.successor?.email ?? '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    analytics.track(AnalyticsEvent.SuccessorSoftAskShown);
  }, []);

  const hasPartialInput = firstName.trim().length > 0 || email.trim().length > 0;
  const hasValidInput =
    firstName.trim().length > 0 && email.trim().length > 0 && emailLooksValid(email);
  const hasInvalidEmail = email.trim().length > 0 && !emailLooksValid(email);

  const doSubmit = async (successor?: { firstName?: string; email?: string }) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      setSuccessor(successor);
      const result = await wizardService.submitAnswers({
        ...answers,
        successor,
      });
      setSubmission(result);
      analytics.track(AnalyticsEvent.WizardCompleted, {
        priority_count: result.priorityTaskKeys.length,
        declined_count: result.declinedTaskIds.length,
        legalzoom_count: result.legalZoomDocTypes.length,
        had_successor: !!successor,
      });
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'WizardPrioritiesReveal' }],
        }),
      );
    } catch (err: any) {
      logger.error('Wizard submit failed', err);
      Alert.alert(
        'Couldn\'t save',
        err?.message
          ? `${err.message}\n\nPlease try again or let us know if this keeps happening.`
          : 'We had trouble saving your setup. Please try again.',
      );
      setSubmitting(false);
    }
  };

  const handleSubmitWithSuccessor = () => {
    if (hasInvalidEmail) {
      Alert.alert('Check the email', 'That doesn\'t look like a valid email address.');
      return;
    }
    if (!hasValidInput) {
      // Fallback: user tapped the primary button with partial data. Treat as skip.
      analytics.track(AnalyticsEvent.SuccessorSoftAskSkipped, { reason: 'partial_input' });
      doSubmit(undefined);
      return;
    }
    analytics.track(AnalyticsEvent.SuccessorSoftAskSubmitted);
    doSubmit({ firstName: firstName.trim(), email: email.trim() });
  };

  const handleSkip = () => {
    analytics.track(AnalyticsEvent.SuccessorSoftAskSkipped, { reason: 'user_skip' });
    doSubmit(undefined);
  };

  return (
    <WizardShell
      stepNumber={WIZARD_STEP}
      totalSteps={TOTAL_STEPS}
      title="Name your successor"
      subtitle="Who's the one person you trust with full access to everything? Most people pick their spouse, an adult child, or their attorney. We'll finish their full setup (passcode, validation) on the next screen."
      primaryLabel={hasValidInput ? 'Finish setup' : 'Skip — finish setup'}
      primaryLoading={submitting}
      onPrimaryPress={hasValidInput ? handleSubmitWithSuccessor : handleSkip}
      onBackPress={() => !submitting && navigation.goBack()}
      secondaryLabel={hasPartialInput ? 'Skip — I\'ll decide later' : undefined}
      onSecondaryPress={hasPartialInput ? handleSkip : undefined}
    >
      <View style={styles.form}>
        <Text style={styles.label}>Their first name</Text>
        <TextInput
          style={styles.input}
          value={firstName}
          onChangeText={setFirstName}
          placeholder="e.g. Sam"
          placeholderTextColor="#9CA3AF"
          autoCapitalize="words"
          editable={!submitting}
        />

        <Text style={styles.label}>Their email</Text>
        <TextInput
          style={[styles.input, hasInvalidEmail && styles.inputError]}
          value={email}
          onChangeText={setEmail}
          placeholder="sam@example.com"
          placeholderTextColor="#9CA3AF"
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          editable={!submitting}
        />
        {hasInvalidEmail ? (
          <Text style={styles.errorText}>That doesn't look like a valid email address.</Text>
        ) : null}

        <Text style={styles.helper}>
          We won't message them yet. Once you finish their full setup (phone, passcode,
          validation question) from the next screen, they'll get an invitation to accept
          the role.
        </Text>
      </View>
    </WizardShell>
  );
};

const styles = StyleSheet.create({
  form: { marginTop: ms(4) },
  label: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginTop: ms(14),
    marginBottom: ms(6),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    paddingHorizontal: ms(14),
    paddingVertical: ms(14),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
  },
  inputError: {
    borderColor: '#E15858',
  },
  errorText: {
    marginTop: ms(6),
    color: '#E15858',
    fontSize: ms(13, 0.3),
  },
  helper: {
    marginTop: ms(16),
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    lineHeight: ms(18, 0.3),
  },
});
